/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { throwError as observableThrowError, Observable } from 'rxjs';

// local imports
import { ApiService } from 'app/services/api.service';
import { ApiResponse } from '@core/models/api-response.model';
import { IBankAccount, ICard } from '@core/models';

@Injectable({
  providedIn: 'root',
})
export class PaymentMethodsService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  static toCard(paymentMethod: IBankAccount & ICard): ICard {
    if (paymentMethod.object === 'card') {
      return paymentMethod as ICard;
    }

    throw new Error('error casting to card');
  }

  static toBankAccount(paymentMethod: IBankAccount & ICard): IBankAccount {
    if (paymentMethod.object === 'bank_account') {
      return paymentMethod as IBankAccount;
    }

    throw new Error('error casting to card');
  }

  createStripeCustomer(clientId: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiService.superAdminApi}/clients/create-stripe-customer`, {
      id: clientId,
    });
  }

  getStripeCustomer(stripeCustomerId: string): Observable<ApiResponse> {
    return this.http
      .get<any>(`${this.apiService.payments}/customers/${stripeCustomerId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getCustomerStripeIdByCustomerId(companyId: string): Observable<ApiResponse> {
    return this.http
      .get<any>(`${this.apiService.payments}/customers/by-company/${companyId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // Bank Accounts
  addBankAccount(stripeCustomerId: string, params: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.payments}/customers/${stripeCustomerId}/bank-account`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  attachBankAccountTokenToCustomer(stripeCustomerId: string, token: any): Observable<ApiResponse> {
    const params = { token };
    return this.http
      .post<ApiResponse>(`${this.apiService.payments}/customers/${stripeCustomerId}/bank-account-token`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deleteBankAccount(stripeCustomerId: string, bankAccountId: string): Observable<ApiResponse> {
    return this.http
      .delete<ApiResponse>(`${this.apiService.payments}/customers/${stripeCustomerId}/bank-account/${bankAccountId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateBankAccount(stripeCustomerId: string, bankAccountId: string, params): Observable<ApiResponse> {
    return this.http
      .put<ApiResponse>(
        `${this.apiService.payments}/customers/${stripeCustomerId}/bank-account/${bankAccountId}`,
        params
      )
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  verifyBankAccount(stripeCustomerId: string, bankAccountId: string, params): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(
        `${this.apiService.payments}/customers/${stripeCustomerId}/bank-account/${bankAccountId}/verify`,
        params
      )
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // Cards
  addCreditCard(clientId: number, token: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/clients/add-payment-method`, {
        clientId,
        token,
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deleteCard(clientId: number, cardId: string): Observable<ApiResponse> {
    return this.http
      .delete<ApiResponse>(`${this.apiService.superAdminApi}/clients/remove-payment-method/${clientId}/${cardId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  charge(params: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.payments}/cards/charge-customer`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getPaymentMethodsByClientId(clientId: number): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/clients/payment-methods/by-client-id/${clientId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getPaymentMethodsByMongoId(mongoId: string): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/clients/payment-methods/by-mongo-id/${mongoId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }
}
